import React, { useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

// Dialog variant: preserves legacy behavior (success/error) via `success` boolean,
// and allows an explicit `variant` prop for neutral/info states (e.g. iOS native
// flow where we don't know if the user actually cancelled or not).
type CancelDialogVariant = "success" | "error" | "info";

interface CancelDialogProps {
  open: boolean;
  onClose: () => void;
  success: boolean; // legacy: true for success, false for error
  message?: string; // Optional custom message
  title?: string; // Optional custom title
  variant?: CancelDialogVariant; // Overrides success/error inference when provided
  autoCloseDelay?: number; // Auto-close after this many milliseconds (default 4000ms = 4 seconds)
}

const CancelDialog: React.FC<CancelDialogProps> = ({ open, onClose, success, message, title, variant, autoCloseDelay = 4000 }) => {
  const resolvedVariant: CancelDialogVariant = variant ?? (success ? "success" : "error");
  const isSuccess = resolvedVariant === "success";
  const isInfo = resolvedVariant === "info";
  const accentColor = isSuccess ? "#36960c" : isInfo ? "#1976d2" : "#d32f2f";
  const defaultTitle = isSuccess ? "Subscription Canceled" : isInfo ? "Heads up" : "Cancellation Failed";
  const defaultMessage = isSuccess
    ? "Your subscription has been canceled successfully."
    : isInfo
      ? "Please review your subscription status."
      : "Your subscription could not be canceled. Please try again.";
  // Auto-close after specified delay
  useEffect(() => {
    if (open) {
      console.log("🎉 CancelDialog opened:", { variant: resolvedVariant, message });
      const timer = setTimeout(() => {
        console.log("⏰ CancelDialog auto-closing after", autoCloseDelay, "ms");
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [open, autoCloseDelay, onClose]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing when clicking outside (backdrop) - only allow closing from X button
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      sx={{
        zIndex: 9999, // Very high z-index to ensure it appears above everything
        "& .MuiDialog-container": {
          zIndex: 9999,
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: PureLightThemeCaaS.colors.alpha.black[100],
          color: PureLightThemeCaaS.colors.alpha.white[100],
          boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
          borderRadius: "8px",
          border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          minWidth: "300px",
          maxWidth: "500px",
          zIndex: 9999,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            position: "fixed",
            backdropFilter: "blur(6px) !important",
            WebkitBackdropFilter: "blur(1px) !important",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 9998, // Just below the dialog
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          color: accentColor,
          fontWeight: "bold",
          fontSize: "16px",
          fontFamily: '"Inter", sans-serif',
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
          pb: 0,
          pt: 0.5,
          pl: 2,
        }}
      >
        {title || defaultTitle}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: PureLightThemeCaaS.colors.alpha.white[100],
            "&:hover": {
              backgroundColor: PureLightThemeCaaS.colors.alpha.black[50],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {isSuccess && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 52,
                color: accentColor,
              }}
            />
          </Box>
        )}
        {isInfo && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <InfoOutlinedIcon
              sx={{
                fontSize: 52,
                color: accentColor,
              }}
            />
          </Box>
        )}
        <Typography
          sx={{
            color: PureLightThemeCaaS.colors.alpha.white[100],
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "1.5",
            fontFamily: '"Inter", sans-serif',
            textAlign: "center",
          }}
        >
          {message || defaultMessage}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default CancelDialog;
